import { arrayToSet, isoDatetimeToDate } from 'api-validations/zod-extra'
import { z } from 'zod'

import { taskSchema } from '../pangea'
import { inputTypeSchema } from './shared'

/**
 * The curated, cross-table headline metrics a report can roll up. Metrics are only summed across
 * applications when the application *type* denotes a single activity (all seeding is one act, all
 * harvest is one act). Liquid/granular volume and mass are deliberately excluded: those types span
 * semantically distinct products (Fungicide vs Herbicide), so a type-level sum is not a total of
 * anything real — a meaningful version would aggregate per application *name*.
 */
export enum ReportTotalMetric {
  DryYield = 'dryYield',
  DryYieldAvg = 'dryYieldAvg',
  Moisture = 'moisture',
  SeedUnits = 'seedUnits',
  PlantedAcres = 'plantedAcres',
  HarvestedAcres = 'harvestedAcres',
  ApplicationDuration = 'applicationDuration'
}

export enum ReportFilters {
  TaskType = 'task',
  ClientFarmField = 'cff',
  DateRange = 'dateRange',
  SerialNumber = 'device',
  InputType = 'input'
}

export const selectedOptionsSchema = z.object({
  filters: z.object({
    [ReportFilters.TaskType]: z.array(taskSchema).default([]),
    [ReportFilters.ClientFarmField]: z
      .object({
        client: z.string().optional(),
        farm: z.string().optional(),
        field: z.string().optional()
      })
      .default({}),
    [ReportFilters.DateRange]: z
      .object({
        start: isoDatetimeToDate,
        end: isoDatetimeToDate
      })
      .optional(),
    [ReportFilters.SerialNumber]: z.array(z.number()).default([]),
    [ReportFilters.InputType]: z.array(inputTypeSchema).default([])
  }),
  toggles: arrayToSet(z.string())
})

export namespace SeasonalReport {
  export type Options = z.infer<typeof selectedOptionsSchema>
}

export enum ReportType {
  SeasonalInputs = 'seasonal-inputs',
  CondensedSeason = 'condensed-season',
  AgronomicActivity = 'agronomic-activity'
}
